import React, { useMemo } from "react";
import cx from "classnames";
import * as Scrivito from "scrivito";
import { Button, Icon } from "components";
import type { IconName } from "components/Icon/iconNames";
import { projectTranslations } from "config/locals/projectTranslations";

import { useTranslate } from "hooks";
import s from "./FilterBtn.module.scss";

type FilterBtnProps = {
  name: React.ReactNode;
  count?: number;
  color?: string;
  icon?: IconName;
  iconPrefix?: string;
  isActive?: boolean;
  onClick: (name: string) => void;
  disabled?: boolean;
  id: string;
};

function FilterBtnComponent({
  name,
  count,
  color,
  icon,
  isActive,
  onClick,
  disabled,
  id,
}: FilterBtnProps) {
  const translate = useTranslate();

  const handleClick = () => {
    onClick(typeof name === "string" ? name : id);
  };

  const translateProp = useMemo(
    () => (typeof name === "string" ? `PROJECT_STATUS_${name}` : ""),
    [name],
  );

  // check for Scrivito edit mode
  const getNameText = (filterName: React.ReactNode): string => {
    if (typeof filterName === "string") {
      return filterName.replace(/-+/g, "_");
    }

    if (filterName && typeof filterName === "object" && "props" in filterName) {
      const props = (filterName as { props?: Record<string, unknown> }).props;
      return (
        (typeof props?.tooltipText === "string" && props.tooltipText) ||
        (typeof props?.text === "string" && props.text) ||
        ""
      );
    }

    return "";
  };

  const hasProjectTranslation = Boolean(
    (projectTranslations as Record<string, string>)[
      translateProp.toUpperCase()
    ],
  );

  const filterName = hasProjectTranslation
    ? translate(translateProp)
    : translate(getNameText(id));

  return (
    <Button
      data-testid={translateProp}
      color="filter"
      active={isActive}
      onClick={handleClick}
      className={cx(
        s.filterBtn,
        {
          [s.checked]: isActive,
          "border-gray-300 bg-white": disabled,
        },
        "text-nowrap",
      )}
      square={true}
      disabled={disabled}
    >
      {color && (
        <div
          className={cx("mr-2 rounded-circle icon-12")}
          style={{ background: color }}
        />
      )}
      {icon && <Icon iconName={icon} />}
      {filterName}
      {typeof count === "number" && ` (${count})`}
    </Button>
  );
}

const ConnectedFilterBtn = Scrivito.connect(FilterBtnComponent);
export const FilterBtn = React.memo(
  ConnectedFilterBtn,
  (prevProps: FilterBtnProps, nextProps: FilterBtnProps) => {
    if (prevProps.name === nextProps.name) {
      return false;
    }
    return true;
  },
);
